import React, { createContext, useContext, useState, useEffect } from 'react';

// Define the color themes
export const themes = {
  theme1: {
    primary: '#5A244A',         // Much darker main purple-pink
    primary_dark: '#3A1429',    // Extremely dark shade, almost black with purple undertone
    secondary: '#B8A3C9',       // Keeping the lighter secondary
    light: '#F8F0F5',           // Keeping the very light background
    neutral1: '#9A7089',        // Keeping the slightly muted mauve
    neutral2: '#2A1221',        // Extremely dark burgundy for maximum contrast
    accent: '#C45598',          // Keeping the bright accent
    text_on_primary: 'rgba(255, 255, 255, 1)',
    text_light_on_primary: 'rgba(255, 255, 255, 0.92)',
    text_on_accent: '#FFFFFF',
    name: 'Lavender Elegance'
  },
  theme2: {
    primary: '#382315',         // Deep brown
    primary_dark: '#2A1A0F',    // Darker brown for gradient
    secondary: '#785940',       // Medium brown
    light: '#D8CEC6',           // Light cream
    neutral1: '#C7AD98',        // Light taupe
    neutral2: '#AB8E73',        // Medium taupe
    accent: '#785940',          // Medium brown for accent
    text_on_primary: 'rgba(255, 255, 255, 0.9)',
    text_light_on_primary: 'rgba(255, 255, 255, 0.7)',
    text_on_accent: '#FFFFFF',  
    name: 'Earthy Comfort'
  },
  theme3: {
    primary: '#0B2E33',         // Deep teal
    primary_dark: '#082328',    // Darker shade for gradient
    secondary: '#4F7C82',       // Medium teal
    light: '#f4feff',           // Light aqua
    neutral1: '#93B1B5',        // Medium aqua-gray
    neutral2: '#4F7C82',        // Medium teal
    accent: '#93B1B5',          // Using neutral1 as accent
    text_on_primary: '#FFFFFF', // White text on dark background
    text_light_on_primary: 'rgba(255, 255, 255, 0.7)', // Softer white text
    text_on_accent: '#0B2E33',  // Dark text on light accent
    name: 'Ocean Depths'
  }
};

// Create the theme context
const ThemeContext = createContext();

// Custom hook to use the theme context
export const useTheme = () => useContext(ThemeContext);

// Provider component
export const ThemeProvider = ({ children }) => {
  // Set default theme from localStorage or use theme2
  const [currentTheme, setCurrentTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'theme2';
  });

  // Apply theme when currentTheme changes
  useEffect(() => {
    applyTheme(currentTheme);
    localStorage.setItem('theme', currentTheme);
  }, [currentTheme]);

  // Function to apply CSS variables
  const applyTheme = (themeName) => {
    const theme = themes[themeName];
    if (!theme) return;

    document.documentElement.style.setProperty('--primary-color', theme.primary);
    document.documentElement.style.setProperty('--primary-dark', theme.primary_dark);
    document.documentElement.style.setProperty('--secondary-color', theme.secondary);
    document.documentElement.style.setProperty('--light-color', theme.light);
    document.documentElement.style.setProperty('--neutral-color-1', theme.neutral1);
    document.documentElement.style.setProperty('--neutral-color-2', theme.neutral2);
    document.documentElement.style.setProperty('--accent-color', theme.accent);
    document.documentElement.style.setProperty('--text-on-primary', theme.text_on_primary);
    document.documentElement.style.setProperty('--text-light-on-primary', theme.text_light_on_primary);
    document.documentElement.style.setProperty('--text-on-accent', theme.text_on_accent);
    
    // Set RGB values by converting hex to RGB
    const primaryRGB = hexToRgb(theme.primary);
    const secondaryRGB = hexToRgb(theme.secondary);
    const lightRGB = hexToRgb(theme.light);
    const neutral1RGB = hexToRgb(theme.neutral1);
    const neutral2RGB = hexToRgb(theme.neutral2);
    const accentRGB = hexToRgb(theme.accent);
    
    document.documentElement.style.setProperty('--primary-color-rgb', primaryRGB);
    document.documentElement.style.setProperty('--secondary-color-rgb', secondaryRGB);
    document.documentElement.style.setProperty('--light-color-rgb', lightRGB);
    document.documentElement.style.setProperty('--neutral-color-1-rgb', neutral1RGB);
    document.documentElement.style.setProperty('--neutral-color-2-rgb', neutral2RGB);
    document.documentElement.style.setProperty('--accent-color-rgb', accentRGB);
  };
  
  // Helper function to convert hex to RGB
  const hexToRgb = (hex) => {
    if (!hex) return '0, 0, 0'; // Default to black if no hex value
    
    try {
      // Remove the hash if it exists
      hex = hex.replace(/^#/, '');
      
      // Handle 3-digit hex
      if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
      }
      
      // Parse the hex values
      let bigint = parseInt(hex, 16);
      let r = (bigint >> 16) & 255;
      let g = (bigint >> 8) & 255;
      let b = bigint & 255;
      
      return `${r}, ${g}, ${b}`;
    } catch (error) {
      console.error('Error parsing hex color:', hex, error);
      return '0, 0, 0'; // Default to black on error
    }
  };

  // Function to change theme
  const setTheme = (themeName) => {
    if (themes[themeName]) {
      setCurrentTheme(themeName);
    }
  };

  // Context value
  const value = {
    currentTheme,
    setTheme,
    themes
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext; 